// ---------------------------------------------------------------------------
// DSS-1: Background type selector — color/gradient/image/video
// ---------------------------------------------------------------------------
import { useCallback, useId } from "react";
import { Video } from "lucide-react";
import type { DssBackgroundEffectPreset } from "@ease/page-builder";
import GradientBuilder, {
  generateGradientCss,
  DEFAULT_GRADIENT,
  type GradientConfig,
} from "./gradient-builder";
import ImageBgControls, {
  generateImageBgCss,
  DEFAULT_IMAGE_BG,
  type ImageBgConfig,
} from "./image-bg-controls";
import { normalizeColorInputValue } from "./color-input-value";

// ── Types ──
export type BackgroundMode = "none" | "color" | "gradient" | "image" | "video";

export interface VideoConfig {
  readonly url: string;
  readonly autoplay: boolean;
  readonly loop: boolean;
  readonly muted: boolean;
}

export interface BackgroundEffectConfig {
  readonly preset: DssBackgroundEffectPreset;
  readonly intensity: number;
  readonly speed: number;
  readonly color: string;
  readonly accentColor: string;
  readonly particleCount: number;
  readonly blendMode: "normal" | "screen" | "overlay" | "soft-light";
}

export interface BackgroundConfig {
  readonly mode: BackgroundMode;
  readonly color: string;
  readonly opacity: number; // 0-100
  readonly gradient: GradientConfig;
  readonly image: ImageBgConfig;
  readonly video: VideoConfig;
  readonly effect: BackgroundEffectConfig;
}

// ── Constants ──
const MODE_LABELS: readonly BackgroundMode[] = ["none", "color", "gradient", "image", "video"];
const MODE_DISPLAY: Record<BackgroundMode, string> = {
  none: "None", color: "Color", gradient: "Gradient", image: "Image", video: "Video",
};

const MIN_OPACITY = 0;
const MAX_OPACITY = 100;
const OPACITY_STEP = 1;
const EFFECT_SLIDER_STEP = 1;

const DEFAULT_VIDEO: VideoConfig = {
  url: "",
  autoplay: true,
  loop: true,
  muted: true,
};

const DEFAULT_EFFECT: BackgroundEffectConfig = {
  preset: "none",
  intensity: 40,
  speed: 4,
  color: "#2563eb",
  accentColor: "#7c3aed",
  particleCount: 12,
  blendMode: "soft-light",
};

const EFFECT_PRESETS: Array<{
  id: DssBackgroundEffectPreset;
  label: string;
  summary: string;
}> = [
  { id: "none", label: "None", summary: "No premium effect overlay" },
  { id: "gradient-shift", label: "Gradient Shift", summary: "Slow premium color rotation" },
  { id: "particle-float", label: "Particles", summary: "Subtle floating field capped for performance" },
  { id: "blob-morph", label: "Blob Morph", summary: "Organic morphing glow backdrop" },
  { id: "aurora", label: "Aurora", summary: "Northern-lights ribbon wash" },
  { id: "mesh-gradient", label: "Mesh", summary: "Soft premium mesh gradient" },
];

const DEFAULT_BACKGROUND: BackgroundConfig = {
  mode: "none",
  color: "#ffffff",
  opacity: 100,
  gradient: DEFAULT_GRADIENT,
  image: DEFAULT_IMAGE_BG,
  video: DEFAULT_VIDEO,
  effect: DEFAULT_EFFECT,
};

// ── NaN guard ──
function safeNum(val: number, min: number, max: number): number {
  const n = Number(val);
  if (Number.isNaN(n)) return min;
  return Math.min(Math.max(n, min), max);
}

// ── CSS Generator ──
export function generateBackgroundCss(config: BackgroundConfig | null): string {
  if (!config || config.mode === "none") return "";

  switch (config.mode) {
    case "color": {
      const opacity = safeNum(config.opacity, MIN_OPACITY, MAX_OPACITY) / 100;
      if (opacity >= 1) return `background-color: ${config.color}`;
      return `background-color: ${config.color}; opacity: ${opacity}`;
    }
    case "gradient":
      return `background-image: ${generateGradientCss(config.gradient)}`;
    case "image":
      return generateImageBgCss(config.image);
    case "video":
      // Video backgrounds are HTML elements, not CSS — return empty
      return "";
    default:
      return "";
  }
}

// ── Props ──
interface BackgroundControlsProps {
  readonly value: BackgroundConfig | null;
  readonly onChange: (config: BackgroundConfig | null) => void;
}

// ── Component ──
export default function BackgroundControls({ value, onChange }: BackgroundControlsProps) {
  const uid = useId();
  const config: BackgroundConfig = {
    ...DEFAULT_BACKGROUND,
    ...value,
    gradient: {
      ...DEFAULT_GRADIENT,
      ...(value?.gradient ?? {}),
    },
    image: {
      ...DEFAULT_IMAGE_BG,
      ...(value?.image ?? {}),
    },
    video: {
      ...DEFAULT_VIDEO,
      ...(value?.video ?? {}),
    },
    effect: {
      ...DEFAULT_EFFECT,
      ...(value?.effect ?? {}),
    },
  };

  const handleModeChange = useCallback(
    (mode: BackgroundMode) => {
      if (mode === "none") {
        onChange(null);
        return;
      }
      onChange({ ...config, mode });
    },
    [config, onChange],
  );

  const handleColorChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ ...config, color: e.target.value });
    },
    [config, onChange],
  );

  const handleOpacityChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const opacity = safeNum(Number(e.target.value), MIN_OPACITY, MAX_OPACITY);
      onChange({ ...config, opacity });
    },
    [config, onChange],
  );

  const handleGradientChange = useCallback(
    (gradient: GradientConfig) => onChange({ ...config, gradient }),
    [config, onChange],
  );

  const handleImageChange = useCallback(
    (image: ImageBgConfig) => onChange({ ...config, image }),
    [config, onChange],
  );

  const handleVideoUrl = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ ...config, video: { ...config.video, url: e.target.value } });
    },
    [config, onChange],
  );

  const handleVideoToggle = useCallback(
    (field: keyof Omit<VideoConfig, "url">) => {
      onChange({ ...config, video: { ...config.video, [field]: !config.video[field] } });
    },
    [config, onChange],
  );

  const handleEffectPreset = useCallback(
    (preset: DssBackgroundEffectPreset) => {
      onChange({
        ...config,
        effect: {
          ...config.effect,
          preset,
          particleCount: preset === "particle-float"
            ? safeNum(config.effect.particleCount, 6, 30)
            : config.effect.particleCount,
        },
      });
    },
    [config, onChange],
  );

  const handleEffectNumber = useCallback(
    (field: keyof Pick<BackgroundEffectConfig, "intensity" | "speed" | "particleCount">, min: number, max: number) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange({
          ...config,
          effect: {
            ...config.effect,
            [field]: safeNum(Number(e.target.value), min, max),
          },
        });
      },
    [config, onChange],
  );

  const handleEffectColor = useCallback(
    (field: keyof Pick<BackgroundEffectConfig, "color" | "accentColor">) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange({
          ...config,
          effect: {
            ...config.effect,
            [field]: e.target.value,
          },
        });
      },
    [config, onChange],
  );

  const handleBlendMode = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      onChange({
        ...config,
        effect: {
          ...config.effect,
          blendMode: e.target.value as BackgroundEffectConfig["blendMode"],
        },
      });
    },
    [config, onChange],
  );

  return (
    <div className="flex flex-col gap-3">
      {/* Mode tabs */}
      <div className="flex gap-0.5">
        {MODE_LABELS.map((m) => {
          const isActive = (config.mode === m) || (m === "none" && !value);
          return (
            <button
              key={m}
              type="button"
              onClick={() => handleModeChange(m)}
              className={[
                "flex-1 rounded px-1 py-1 text-[10px] font-medium transition-colors",
                isActive
                  ? "bg-[var(--color-primary)] text-[var(--color-primary-foreground)]"
                  : "bg-[var(--color-muted)] text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]/80",
              ].join(" ")}
            >
              {MODE_DISPLAY[m]}
            </button>
          );
        })}
      </div>

      {/* Color mode */}
      {config.mode === "color" && (
        <div className="flex flex-col gap-2.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-muted)]/40 p-3">
          <SliderRow id={`${uid}-color`} label="Color" type="color" value={config.color} onChange={handleColorChange} />
          <div className="flex items-center gap-2">
            <label htmlFor={`${uid}-opacity`} className="w-12 shrink-0 text-[10px] font-medium text-[var(--color-muted-foreground)]">Opacity</label>
            <input id={`${uid}-opacity`} type="range" min={MIN_OPACITY} max={MAX_OPACITY} step={OPACITY_STEP} value={config.opacity} onChange={handleOpacityChange} className="h-1 flex-1 cursor-pointer appearance-none rounded-full bg-[var(--color-border)] accent-[var(--color-primary)]" />
            <span className="w-10 text-right text-[10px] tabular-nums text-[var(--color-muted-foreground)]">{safeNum(config.opacity, MIN_OPACITY, MAX_OPACITY)}%</span>
          </div>
          <div className="h-8 w-full rounded-md border border-[var(--color-border)]" style={{ backgroundColor: config.color, opacity: safeNum(config.opacity, MIN_OPACITY, MAX_OPACITY) / 100 }} />
        </div>
      )}

      {/* Gradient mode */}
      {config.mode === "gradient" && (
        <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-muted)]/40 p-3">
          <GradientBuilder
            value={config.gradient}
            onChange={handleGradientChange}
          />
        </div>
      )}

      {/* Image mode */}
      {config.mode === "image" && (
        <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-muted)]/40 p-3">
          <ImageBgControls
            value={config.image}
            onChange={handleImageChange}
          />
        </div>
      )}

      {/* Video mode */}
      {config.mode === "video" && (
        <div className="flex flex-col gap-2.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-muted)]/40 p-3">
          <div className="flex flex-col gap-1">
            <label
              htmlFor={`${uid}-video-url`}
              className="text-[10px] font-medium text-[var(--color-muted-foreground)]"
            >
              Video URL
            </label>
            <div className="flex items-center gap-1.5">
              <Video className="h-3.5 w-3.5 shrink-0 text-[var(--color-muted-foreground)]" />
              <input
                id={`${uid}-video-url`}
                type="text"
                value={config.video.url}
                onChange={handleVideoUrl}
                placeholder="https://..."
                className="w-full rounded border border-[var(--color-border)] bg-[var(--color-background)] px-2 py-1 text-[10px] text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)]"
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <VideoToggle
              label="Autoplay"
              checked={config.video.autoplay}
              onChange={() => handleVideoToggle("autoplay")}
              id={`${uid}-autoplay`}
            />
            <VideoToggle
              label="Loop"
              checked={config.video.loop}
              onChange={() => handleVideoToggle("loop")}
              id={`${uid}-loop`}
            />
            <VideoToggle
              label="Muted"
              checked={config.video.muted}
              onChange={() => handleVideoToggle("muted")}
              id={`${uid}-muted`}
            />
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-muted)]/40 p-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--color-muted-foreground)]">
            Premium Effect
          </p>
          <p className="mt-1 text-[10px] text-[var(--color-muted-foreground)]">
            One-click overlays for premium backgrounds. Reduced-motion safe. Particles capped at 30.
          </p>
        </div>

        <div className="grid gap-1.5 sm:grid-cols-2">
          {EFFECT_PRESETS.map((preset) => {
            const isActive = config.effect.preset === preset.id;
            return (
              <button
                key={preset.id}
                type="button"
                onClick={() => handleEffectPreset(preset.id)}
                className={[
                  "rounded-lg border px-3 py-2 text-left transition-colors",
                  isActive
                    ? "border-[var(--color-primary)] bg-[var(--color-primary)]/10"
                    : "border-[var(--color-border)] bg-[var(--color-background)] hover:border-[var(--color-primary)]/40",
                ].join(" ")}
              >
                <div className="text-[11px] font-medium text-[var(--color-foreground)]">{preset.label}</div>
                <div className="mt-1 text-[10px] text-[var(--color-muted-foreground)]">{preset.summary}</div>
              </button>
            );
          })}
        </div>

        {config.effect.preset !== "none" ? (
          <div className="flex flex-col gap-2.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-3">
            <RangeRow
              id={`${uid}-effect-intensity`}
              label="Intensity"
              min={0}
              max={100}
              step={EFFECT_SLIDER_STEP}
              value={config.effect.intensity}
              onChange={handleEffectNumber("intensity", 0, 100)}
            />
            <RangeRow
              id={`${uid}-effect-speed`}
              label="Speed"
              min={1}
              max={10}
              step={EFFECT_SLIDER_STEP}
              value={config.effect.speed}
              onChange={handleEffectNumber("speed", 1, 10)}
            />
            {config.effect.preset === "particle-float" ? (
              <RangeRow
                id={`${uid}-effect-particles`}
                label="Particles"
                min={6}
                max={30}
                step={EFFECT_SLIDER_STEP}
                value={config.effect.particleCount}
                onChange={handleEffectNumber("particleCount", 6, 30)}
              />
            ) : null}
            <SliderRow id={`${uid}-effect-color`} label="Color" type="color" value={config.effect.color} onChange={handleEffectColor("color")} />
            <SliderRow id={`${uid}-effect-accent`} label="Accent" type="color" value={config.effect.accentColor} onChange={handleEffectColor("accentColor")} />
            <div className="flex items-center gap-2">
              <label htmlFor={`${uid}-effect-blend`} className="w-12 shrink-0 text-[10px] font-medium text-[var(--color-muted-foreground)]">Blend</label>
              <select
                id={`${uid}-effect-blend`}
                value={config.effect.blendMode}
                onChange={handleBlendMode}
                className="w-full rounded border border-[var(--color-border)] bg-[var(--color-background)] px-2 py-1 text-[10px] text-[var(--color-foreground)]"
              >
                <option value="soft-light">Soft Light</option>
                <option value="screen">Screen</option>
                <option value="overlay">Overlay</option>
                <option value="normal">Normal</option>
              </select>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ── Color input row ──
function SliderRow({ id, label, type, value, onChange }: {
  readonly id: string; readonly label: string; readonly type: string;
  readonly value: string; readonly onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  const inputValue = type === "color" ? normalizeColorInputValue(value) : value;

  return (
    <div className="flex items-center gap-2">
      <label htmlFor={id} className="w-12 shrink-0 text-[10px] font-medium text-[var(--color-muted-foreground)]">{label}</label>
      <input id={id} type={type} value={inputValue} onChange={onChange} className="h-6 w-8 cursor-pointer rounded border border-[var(--color-border)] bg-transparent p-0" />
      <span className="text-[10px] tabular-nums text-[var(--color-muted-foreground)]">{value}</span>
    </div>
  );
}

function RangeRow({ id, label, value, min, max, step, onChange }: {
  readonly id: string;
  readonly label: string;
  readonly value: number;
  readonly min: number;
  readonly max: number;
  readonly step: number;
  readonly onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <label htmlFor={id} className="w-12 shrink-0 text-[10px] font-medium text-[var(--color-muted-foreground)]">{label}</label>
      <input id={id} type="range" min={min} max={max} step={step} value={value} onChange={onChange} className="h-1 flex-1 cursor-pointer appearance-none rounded-full bg-[var(--color-border)] accent-[var(--color-primary)]" />
      <span className="w-10 text-right text-[10px] tabular-nums text-[var(--color-muted-foreground)]">{value}</span>
    </div>
  );
}

// ── Toggle row for video settings ──
function VideoToggle({ label, checked, onChange, id }: {
  readonly label: string; readonly checked: boolean; readonly onChange: () => void; readonly id: string;
}) {
  return (
    <div className="flex items-center justify-between">
      <label htmlFor={id} className="text-[10px] font-medium text-[var(--color-muted-foreground)]">{label}</label>
      <button id={id} type="button" role="switch" aria-checked={checked} onClick={onChange}
        className={["relative h-4 w-7 rounded-full transition-colors", checked ? "bg-[var(--color-primary)]" : "bg-[var(--color-border)]"].join(" ")}>
        <span className="absolute top-0.5 left-0.5 h-3 w-3 rounded-full bg-white transition-transform" style={{ transform: checked ? "translateX(12px)" : "translateX(0)" }} />
      </button>
    </div>
  );
}

export { DEFAULT_BACKGROUND };
export type { GradientConfig, ImageBgConfig };
